import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Chat } from './chatStore';

/**
 * Navigation context for the raw view — tracks the ordered PK list and the
 * currently-selected position so prev/next buttons respect AG Grid filters/sort.
 */
interface ContextInfo {
    idList: string[];
    selectedId: string;
    currentIndex: number;
}

/**
 * A grouped reference entry (conversation or conversation-day) passed to the
 * communication raw view. Backend shape — fields accessed on the frontend only.
 */
interface ReferenceGroup {
    type: string;
    reference_unit: string;
    display_identifier?: string;
    content_hash?: string;
    metadata?: Record<string, unknown>;
}

export const useDataViewStore = defineStore('dataView', () => {
    console.log('[dataViewStore] Store initialized');

    const showRawViewModal = ref(false);
    const currentCollectionId = ref<string | null>(null);
    const currentPrimaryKeyValue = ref<string | null>(null);
    const currentContextInfo = ref<ContextInfo | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    const isDetailsViewerVisible = ref(false);
    const selectedChatForDetails = ref<Chat | null>(null);
    // Whether the per-type RawViewContainer (the inline detail view inside
    // the references panel) is shown for the active item. Lifted from
    // MatterPage's local ref so the URL sync composable / cold deep links
    // can drive it without provide/inject gymnastics.
    const showDetailView = ref(false);

    // References-view selection state. Lifted from ReferencesPanel so the
    // URL sync composable can drive them without component-local refs.
    const selectedReferenceType = ref<string | null>(null);
    const selectedReferenceUnit = ref<string | null>(null);

    // Hash of an item to auto-open once references finish loading. Set when
    // a deep-link URL has `?item=<content_hash>`; cleared by ReferencesPanel
    // after it tries to open the raw view.
    const pendingItemHash = ref<string | null>(null);

    const currentReferenceIdList = ref<string[]>([]);

    const showCommunicationRawView = ref(false);
    const communicationGroups = ref<ReferenceGroup[]>([]);
    const currentGroupIndex = ref(0);

    const isRawViewModalOpen = computed(() => showRawViewModal.value);

    function openRawView(
        collectionId: string,
        primaryKeyValue: string | number,
        contextInfo: { idList?: string[]; currentIndex: number } | null = null,
    ) {
        currentCollectionId.value = collectionId;
        currentPrimaryKeyValue.value = String(primaryKeyValue);

        if (contextInfo) {
            currentContextInfo.value = {
                idList: contextInfo.idList || [],
                selectedId: String(primaryKeyValue),
                currentIndex: contextInfo.currentIndex,
            };
        } else {
            currentContextInfo.value = null;
        }

        showRawViewModal.value = true;
        error.value = null;
    }

    function closeRawView() {
        showRawViewModal.value = false;
        currentCollectionId.value = null;
        currentPrimaryKeyValue.value = null;
        currentContextInfo.value = null;
    }

    function navigateRawView(newPrimaryKeyValue: string | number, newIndex: number) {
        currentPrimaryKeyValue.value = String(newPrimaryKeyValue);

        if (currentContextInfo.value) {
            currentContextInfo.value = {
                ...currentContextInfo.value,
                selectedId: String(newPrimaryKeyValue),
                currentIndex: newIndex,
            };
        }
    }

    function setReferenceIdList(idList: string[]) {
        currentReferenceIdList.value = idList;
    }

    function openDetailsViewer(chat: Chat) {
        selectedChatForDetails.value = chat;
        isDetailsViewerVisible.value = true;
        error.value = null;
    }

    function closeDetailsViewer() {
        isDetailsViewerVisible.value = false;
        selectedChatForDetails.value = null;
        selectedReferenceType.value = null;
        selectedReferenceUnit.value = null;
        pendingItemHash.value = null;
        showDetailView.value = false;
        showCommunicationRawView.value = false;
        communicationGroups.value = [];
        currentGroupIndex.value = 0;
    }

    function openCommunicationRawView(groupedReferences: ReferenceGroup[], groupIndex: number) {
        communicationGroups.value = groupedReferences;
        currentGroupIndex.value = groupIndex;
        showCommunicationRawView.value = true;
        error.value = null;
    }

    function closeCommunicationRawView() {
        showCommunicationRawView.value = false;
        communicationGroups.value = [];
        currentGroupIndex.value = 0;
    }

    function navigateCommunicationRawView(newIndex: number) {
        if (newIndex >= 0 && newIndex < communicationGroups.value.length) {
            currentGroupIndex.value = newIndex;
        }
    }

    return {
        showRawViewModal,
        currentCollectionId,
        currentPrimaryKeyValue,
        currentContextInfo,
        loading,
        error,
        isDetailsViewerVisible,
        selectedChatForDetails,
        showDetailView,
        selectedReferenceType,
        selectedReferenceUnit,
        pendingItemHash,
        currentReferenceIdList,
        showCommunicationRawView,
        communicationGroups,
        currentGroupIndex,
        isRawViewModalOpen,
        openRawView,
        closeRawView,
        navigateRawView,
        setReferenceIdList,
        openDetailsViewer,
        closeDetailsViewer,
        openCommunicationRawView,
        closeCommunicationRawView,
        navigateCommunicationRawView,
    };
});
